import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Spinner } from 'react-bootstrap';
import { BaseURL } from 'shared/utils/Endpoints';
import styles from './style.module.scss';

export default function CustomImageLightbox({
    images = [],
    startIndex = 0,
    isOpen,
    onClose,
    alt = "wisetech"
}) {
    const [activeIndex, setActiveIndex] = useState(startIndex);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const [downloadingAll, setDownloadingAll] = useState(false);
    const [downloadAllProgress, setDownloadAllProgress] = useState('');

    useEffect(() => {
        setActiveIndex(startIndex);
    }, [startIndex]);

    useEffect(() => {
        if (isOpen) {
            // Prevent background scrolling
            const originalOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = originalOverflow;
            };
        }
    }, [isOpen]);

    // Handle Keyboard Navigation
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e) => {
            if (e.key === 'ArrowRight') {
                handleNext();
            } else if (e.key === 'ArrowLeft') {
                handlePrev();
            } else if (e.key === 'Escape') {
                onClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, activeIndex, images]);

    const handleNext = () => {
        if (images.length <= 1) return;
        setLoading(true);
        setActiveIndex((prev) => (prev + 1) % images.length);
    };

    const handlePrev = () => {
        if (images.length <= 1) return;
        setLoading(true);
        setActiveIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    const handleDownload = async (e) => {
        e.stopPropagation();
        const imageUrl = images[activeIndex];
        if (!imageUrl) return;

        setDownloading(true);
        try {
            let downloadUrl = imageUrl;
            // Only route through our backend proxy if it's an external HTTP/HTTPS URL
            if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
                downloadUrl = `${BaseURL}media/download?url=${encodeURIComponent(imageUrl)}`;
            }

            const response = await fetch(downloadUrl);
            if (!response.ok) throw new Error('Download request failed');
            
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = blobUrl;
            
            // Extract a clean filename from the URL, or use a default one
            const urlParts = imageUrl.split('/');
            const lastPart = urlParts[urlParts.length - 1];
            const filename = lastPart ? lastPart.split('?')[0] : `wisetech-image-${activeIndex + 1}.jpg`;
            
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Failed to download image via proxy/blob, falling back to direct download:', error);
            // Graceful fallback to browser default (will trigger direct download or open in tab)
            if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
                window.open(`${BaseURL}media/download?url=${encodeURIComponent(imageUrl)}`, '_blank');
            } else {
                window.open(imageUrl, '_blank');
            }
        } finally {
            setDownloading(false);
        }
    };

    const handleDownloadAll = async (e) => {
        e.stopPropagation();
        if (images.length === 0) return;

        setDownloadingAll(true);
        try {
            for (let i = 0; i < images.length; i++) {
                const imageUrl = images[i];
                setDownloadAllProgress(`${i + 1}/${images.length}`);
                
                try {
                    let downloadUrl = imageUrl;
                    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
                        downloadUrl = `${BaseURL}media/download?url=${encodeURIComponent(imageUrl)}`;
                    }

                    const response = await fetch(downloadUrl);
                    if (!response.ok) throw new Error('Download failed');
                    
                    const blob = await response.blob();
                    const blobUrl = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = blobUrl;
                    
                    const urlParts = imageUrl.split('/');
                    const lastPart = urlParts[urlParts.length - 1];
                    const filename = lastPart ? lastPart.split('?')[0] : `wisetech-image-${i + 1}.jpg`;
                    
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(blobUrl);
                } catch (err) {
                    console.error(`Failed to download image ${i + 1} via proxy/blob:`, err);
                    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
                        window.open(`${BaseURL}media/download?url=${encodeURIComponent(imageUrl)}`, '_blank');
                    } else {
                        window.open(imageUrl, '_blank');
                    }
                }
                
                // Add a small delay between downloads to prevent browser spam blocking
                if (i < images.length - 1) {
                    await new Promise(resolve => setTimeout(resolve, 400));
                }
            }
        } catch (error) {
            console.error('Failed to download all images:', error);
        } finally {
            setDownloadingAll(false);
            setDownloadAllProgress('');
        }
    };

    if (!isOpen || images.length === 0) return null;

    const currentImage = images[activeIndex];

    return createPortal(
        <div className={styles.LightboxBackdrop} onClick={onClose}>
            {/* Header / Top Control Bar */}
            <div className={styles.TopBar} onClick={(e) => e.stopPropagation()}>
                <span className={styles.Counter}>
                    Image {activeIndex + 1} of {images.length}
                </span>
                
                <div className={styles.Actions}>
                    {images.length > 1 && (
                        <button 
                            className={styles.ActionButton} 
                            onClick={handleDownloadAll} 
                            title="Download All Images"
                            disabled={downloadingAll || downloading}
                            style={{ gap: '6px', padding: '0 12px', width: 'auto', minWidth: '40px', fontSize: '13px' }}
                        >
                            {downloadingAll ? (
                                <>
                                    <Spinner animation="border" size="sm" variant="light" />
                                    <span style={{ fontWeight: '600' }}>{downloadAllProgress}</span>
                                </>
                            ) : (
                                <>
                                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '18px', height: '18px' }}>
                                        <path d="M12 13V3M12 13L8 9M12 13L16 9M4 17H20C21.1046 17 22 17.8954 22 19V20C22 20.5523 21.5523 21 20 21H4C2.44772 21 2 20.5523 2 20V19C2 17.8954 2.89543 17 4 17Z" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    <span style={{ fontWeight: '500' }}>Download All</span>
                                </>
                            )}
                        </button>
                    )}

                    <button 
                        className={styles.ActionButton} 
                        onClick={handleDownload} 
                        title="Download Image"
                        disabled={downloading || downloadingAll}
                    >
                        {downloading ? (
                            <Spinner animation="border" size="sm" variant="light" />
                        ) : (
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 15V3M12 15L8 11M12 15L16 11M2 17V19C2 20.1046 2.89543 21 4 21H20C21.1046 21 22 20.1046 22 19V17" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        )}
                    </button>
                    
                    <button className={styles.ActionButton} onClick={onClose} title="Close Lightbox">
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>
            </div>

            {/* Left Nav Arrow */}
            {images.length > 1 && (
                <button 
                    className={`${styles.NavArrow} ${styles.LeftArrow}`} 
                    onClick={(e) => {
                        e.stopPropagation();
                        handlePrev();
                    }}
                    title="Previous Image"
                >
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15 19L8 12L15 5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>
            )}

            {/* Main Image Viewport */}
            <div className={styles.Viewport} onClick={onClose}>
                {loading && (
                    <div className={styles.SpinnerContainer}>
                        <Spinner animation="border" variant="light" />
                    </div>
                )}
                <img
                    src={currentImage}
                    alt={alt}
                    className={`${styles.LightboxImage} ${loading ? styles.ImageLoading : styles.ImageLoaded}`}
                    onLoad={() => setLoading(false)}
                    onClick={(e) => e.stopPropagation()}
                />
            </div>

            {/* Right Nav Arrow */}
            {images.length > 1 && (
                <button 
                    className={`${styles.NavArrow} ${styles.RightArrow}`} 
                    onClick={(e) => {
                        e.stopPropagation();
                        handleNext();
                    }}
                    title="Next Image"
                >
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>
            )}
        </div>,
        document.body
    );
}
